import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { Loader2 } from 'lucide-react';
import * as Yup from 'yup';

import { CreateUserHeader } from './CreateUserHeader';
import { StepperHeader } from './StepperHeader';
import ContactDetailForm from './ContactDetailForm';
import PreviewAndSave from './PreviewAndSave';
import RoleSelectionForm from './RoleSelectionForm';
import { useCreateUser } from '../Providers/CreateUserProvider/CreateUserContext';
import { getDepartments } from '../../services/departmentService';
import { postCreateUser, postEditUser } from '../../services/userService';

//  Props type directly inline with component
type CreateUserModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onUserCreated?: (action: 'create' | 'edit') => void;
  onError?: (message: string) => void;
  editData?: any | null;
};

const CreateUserModal: React.FC<CreateUserModalProps> = ({ isOpen, onClose, onUserCreated, onError, editData }) => {
  const [step, setStep] = useState(0);
  const [departments, setDepartments] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { createUserData, setCreateUserData, resetCreateUserData } = useCreateUser();

  useEffect(() => {
    if (isOpen) {
      fetchDepartments();

      if (editData) {
        setCreateUserData({
          userId: editData.data.id,
          userName: editData.data.name || '',
          email: editData.data.email || '',
          mobileNo: editData.data.phone || '',
          department: editData.data.department_id || '',
          departmentName: editData.data.department?.name || '',
          password: '',
          confirmPassword: '',
          role: editData.data.user_type_id || '',
          roleName: editData.data.role || '',
          shift: editData.data.shift || '',
          address: editData.data.address || '',
          street: editData.data.street || '',
          city: editData.data.city || '',
          state: editData.data.state || '',
          country: editData.data.country || '',
        });
      } else {
        resetCreateUserData();
      }
    }
  }, [isOpen, editData]);

  const fetchDepartments = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getDepartments();
      setDepartments(data || []);
    } catch (err) {
      console.error('Failed to load departments:', err);
      setError('Failed to load departments');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const steps = ['Info', 'Contact Details', 'Role', 'Preview & Save'];

  const validationSchema = Yup.object({
    userName: Yup.string().required('User name is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    //mobileNo: Yup.string()
    //.matches(/^[0-9]{10}$/, 'Enter a valid 10-digit number')
    //.required('Mobile number is required'),
    //department: Yup.number().required('Select a department').typeError('Select a department'),
    password: editData
      ? Yup.string().nullable()
      : Yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
    confirmPassword: editData
      ? Yup.string().nullable()
      : Yup.string()
          .oneOf([Yup.ref('password')], 'Passwords must match')
          .required('Confirm Password is required'),
  });

  const transformPayload = () => {
    const payload: any = {
      name: createUserData.userName,
      email: createUserData.email,
      user_type_id: createUserData.role,
      role: createUserData.role,
      phone: createUserData.mobileNo,
      shift: createUserData.shift,
      address: createUserData.address,
      street: createUserData.street,
      city: createUserData.city,
      state: createUserData.state,
      country: createUserData.country,
      department_id: createUserData.department,
    };

    if (createUserData.password && createUserData.password.trim() !== '') {
      payload.password = createUserData.password;
    }

    return payload;
  };

  const handleClose = () => {
    setStep(0);
    resetCreateUserData();
    onClose();
  };

  const handleSubmit = async (values: typeof createUserData) => {
    setCreateUserData(values);

    if (step < steps.length - 1) {
      setStep((prev) => prev + 1);
    } else {
      const payload = transformPayload();
      try {
        if (editData?.data?.id) {
          await postEditUser(editData.data.id, payload);
          onUserCreated?.('edit');
        } else {
          await postCreateUser(payload);
          onUserCreated?.('create');
        }
        resetCreateUserData();
        handleClose();
      } catch (err: any) {
        console.error('Failed to create/edit user:', err);
        const errorMessage = err?.response?.data?.message || err?.message || 'Failed to save user data';
        onError?.(errorMessage);
      }
    }
  };

  const handlePrev = () => step > 0 && setStep((prev) => prev - 1);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
      <div className="bg-white w-[800px] rounded-lg shadow-lg">
        <CreateUserHeader onClose={handleClose} editData={editData} />
        <StepperHeader steps={steps} activeStep={step} />

        <Formik enableReinitialize initialValues={createUserData} validationSchema={validationSchema} onSubmit={handleSubmit}>
          {({ values, setFieldValue, isSubmitting }) => (
            <Form className="p-8">
              {step === 0 && (
                <div className="grid grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      User Name
                      <span className="text-red-500">*</span>
                    </label>
                    <Field
                      name="userName"
                      placeholder="Enter user name"
                      className="w-full border border-gray-300 rounded-md px-3 py-2  outline-none focus:outline-none focus:ring-0 focus:border-gray-300 bg-gray-100"
                    />
                    <ErrorMessage name="userName" component="div" className="text-red-500 text-xs mt-1" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Email
                      <span className="text-red-500">*</span>
                    </label>
                    <Field
                      name="email"
                      placeholder="Enter email"
                      className="w-full border border-gray-300 rounded-md px-3 py-2  outline-none focus:outline-none focus:ring-0 focus:border-gray-300 bg-gray-100"
                    />
                    <ErrorMessage name="email" component="div" className="text-red-500 text-xs mt-1" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Password
                      <span className="text-red-500">*</span>
                    </label>
                    <Field
                      name="password"
                      type="password"
                      placeholder="Enter password"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:outline-none focus:ring-0 focus:border-gray-300 bg-gray-100"
                    />
                    <ErrorMessage name="password" component="div" className="text-red-500 text-xs mt-1" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Confirm Password
                      <span className="text-red-500">*</span>
                    </label>
                    <Field
                      name="confirmPassword"
                      type="password"
                      placeholder="Confirm password"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:outline-none focus:ring-0 focus:border-gray-300 bg-gray-100"
                    />
                    <ErrorMessage name="confirmPassword" component="div" className="text-red-500 text-xs mt-1" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Mobile No</label>
                    <Field
                      name="mobileNo"
                      placeholder="Enter mobile number"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:outline-none focus:ring-0 focus:border-gray-300 bg-gray-100"
                    />
                    <ErrorMessage name="mobileNo" component="div" className="text-red-500 text-xs mt-1" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Department</label>
                    {loading ? (
                      <p className="text-gray-500 text-sm">Loading departments...</p>
                    ) : error ? (
                      <p className="text-red-500 text-sm">{error}</p>
                    ) : (
                      <Field
                        as="select"
                        name="department"
                        className={`w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100 
                        ${!values.department ? 'text-gray-400' : 'text-gray-700'}
                        outline-none focus:outline-none focus:ring-0 focus:border-gray-300`}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                          const selectedId = e.target.value;
                          const selectedDept = departments.find((d) => d.id === Number(selectedId));
                          setFieldValue('department', selectedId);
                          setFieldValue('departmentName', selectedDept ? selectedDept.name : '');
                        }}
                      >
                        <option value="" disabled hidden>
                          Select Department
                        </option>
                        {departments.map((dept) => (
                          <option key={dept.id} value={dept.id}>
                            {dept.name}
                          </option>
                        ))}
                      </Field>
                    )}
                    <ErrorMessage name="department" component="div" className="text-red-500 text-xs mt-1" />
                  </div>
                </div>
              )}

              {step === 1 && <ContactDetailForm />}
              {step === 2 && <RoleSelectionForm />}
              {step === 3 && <PreviewAndSave />}

              <div className="flex justify-end gap-3 mt-8">
                {step > 0 && (
                  <button
                    type="button"
                    onClick={handlePrev}
                    className="px-5 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100"
                  >
                    Back
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-5 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-2 bg-[#e84545] text-white rounded-md hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {isSubmitting && step === steps.length - 1 && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isSubmitting && step === steps.length - 1
                    ? editData
                      ? 'Updating...'
                      : 'Saving...'
                    : step === steps.length - 1
                      ? editData
                        ? 'Update'
                        : 'Save'
                      : 'Next'}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default CreateUserModal;
